import {
  Box,
  Button,
  Card,
  CloseButton,
  Dialog,
  Heading,
  HStack,
  Image,
  Portal,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Bot,
  Brain,
  Cable,
  Calculator,
  Check,
  Cpu,
  Database,
  type LucideIcon,
  Network,
  PlugZap,
  Radio,
  Scale,
  Shield,
  Tags,
  Workflow,
  ZoomIn,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'

import visionImage from '@/assets/Platform-Page-Diagram-LIGHT.png'
import { InsightCard, PageHeader, RevealStack, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/hivemqvision')({
  component: HiveMQVisionPage,
})

interface ZoomableMediaProps {
  src: string
  alt: string
  h?: string
  onZoom: (src: string, alt: string) => void
}

function ZoomableMedia({ src, alt, h = '500px', onZoom }: ZoomableMediaProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: pre-existing pattern predating this restyle; already keyboard/aria accessible below
    <Box
      position="relative"
      cursor="zoom-in"
      onClick={() => onZoom(src, alt)}
      role="button"
      tabIndex={0}
      aria-label={`Zoom into ${alt}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onZoom(src, alt)
        }
      }}
      _hover={{
        '& > .zoom-overlay': {
          opacity: 1,
        },
      }}
    >
      <Image src={src} alt={alt} h={h} objectFit="contain" />
      <Box
        className="zoom-overlay"
        position="absolute"
        top={2}
        right={2}
        bg="blackAlpha.600"
        borderRadius="md"
        p={2}
        opacity={0}
        transition="opacity 0.2s"
      >
        <ZoomIn size={20} color="white" />
      </Box>
    </Box>
  )
}

interface PillarCardProps {
  icon: LucideIcon
  title: string
  description: string
  /** Crisp one-line framing from the "Three Coordinated Layers" narrative. */
  tagline: string
  features: string[]
  products: string[]
  footer?: ReactNode
}

function PillarCard({
  icon: Icon,
  title,
  description,
  tagline,
  features,
  products,
  footer,
}: PillarCardProps) {
  return (
    <Card.Root
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-6px)', shadow: 'xl' }}
      h="100%"
    >
      <Card.Body p={6}>
        <VStack align="start" gap={4} h="100%">
          <HStack gap={3}>
            <Box p={3} bg="brand.solid" borderRadius="xl">
              <Icon size={28} />
            </Box>
            <VStack align="start" gap={0}>
              <Heading size="md">{title}</Heading>
              <Text fontSize="sm" color="content.secondary">
                {description}
              </Text>
            </VStack>
          </HStack>
          <Box borderLeftWidth="3px" borderLeftColor="brand.solid" pl={3} py={0.5}>
            <Text fontSize="sm" fontStyle="italic" color="content.secondary">
              &ldquo;{tagline}&rdquo;
            </Text>
          </Box>
          <VStack align="start" gap={2} w="100%">
            {features.map((feature) => (
              <HStack key={feature} gap={2}>
                <Box p={0.5} bg="green.100" borderRadius="full">
                  <Check size={12} color="var(--chakra-colors-green-600)" />
                </Box>
                <Text fontSize="sm">{feature}</Text>
              </HStack>
            ))}
          </VStack>
          <HStack gap={2} flexWrap="wrap" pt={2}>
            {products.map((product) => (
              <Box
                key={product}
                px={2}
                py={0.5}
                bg="gray.100"
                borderRadius="md"
                _dark={{ bg: 'gray.700' }}
              >
                <Text fontSize="xs" fontWeight="medium">
                  {product}
                </Text>
              </Box>
            ))}
          </HStack>
          {footer ? (
            <Box mt="auto" pt={1}>
              {footer}
            </Box>
          ) : null}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface PrincipleCardProps {
  icon: LucideIcon
  title: string
  description: string
}

function PrincipleCard({ icon: Icon, title, description }: PrincipleCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}>
      <Card.Body p={5}>
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface UnifiedPhaseProps {
  accent: string
  colorPalette: string
  icon: LucideIcon
  badgeLabel: string
  title: string
  description: string
  actionLabel: string
  onClick: () => void
}

interface ProgressionStepProps {
  icon: LucideIcon
  title: string
  description: string
}

function ProgressionStepCard({ icon: Icon, title, description }: ProgressionStepProps) {
  return (
    <Card.Root
      flex="1"
      minW="180px"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
      h="100%"
    >
      <Card.Body p={4}>
        <VStack align="start" gap={2}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={18} />
          </Box>
          <Text fontWeight="bold" fontSize="sm">
            {title}
          </Text>
          <Text fontSize="xs" color="content.secondary">
            {description}
          </Text>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

function ProgressionArrow() {
  return (
    <Box
      display={{ base: 'none', md: 'flex' }}
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      color="content.tertiary"
    >
      <ArrowRight size={18} />
    </Box>
  )
}

function HiveMQVisionPage() {
  const navigate = useNavigate()
  const [zoomMedia, setZoomMedia] = useState<{ src: string; alt: string } | null>(null)

  const handleZoom = (src: string, alt: string) => {
    setZoomMedia({ src, alt })
  }

  const handleCloseZoom = () => {
    setZoomMedia(null)
  }

  const pillars: PillarCardProps[] = [
    {
      icon: Radio,
      title: 'Data Streaming',
      description: 'Reliably stream real-time data from edge to site to cloud',
      tagline: 'We move operational data reliably.',
      features: [
        'Connect millions of devices and systems for elastic scalability and future-proof growth with MQTT',
        'Integrate OT and IT systems across any device or environment for seamless edge-to-cloud connectivity',
        'Stream data with guaranteed delivery and fault tolerance for a resilient, trusted data backbone',
      ],
      products: ['HiveMQ Broker', 'HiveMQ Edge'],
    },
    {
      icon: Database,
      title: 'Data Intelligence',
      description: 'Add structure, meaning, and governance for a shared understanding',
      tagline: 'We make it understandable and trustworthy.',
      features: [
        'Unify and contextualize data with a UNS for real-time access and visibility',
        'Govern, transform, and validate data for clean, trusted pipelines from edge to cloud',
        'Deliver distributed intelligence for real-time insights at the source',
        'Auto-discover unknown data, compute KPIs at the edge, and onboard new sites in hours with repeatable Data Models',
      ],
      products: ['HiveMQ Pulse', 'HiveMQ Data Hub'],
    },
    {
      icon: Bot,
      title: 'Agentic AI',
      description: 'Build and govern industrial AI agents for safe, closed-loop action',
      tagline: 'We turn it into safe action.',
      features: [
        'Enable trusted delegation  -  domain experts describe a goal, agents execute it safely and reliably',
        'Autonomous agents monitor operations, detect anomalies, and trigger corrective workflows',
        'Achieve AI inference at the edge and site  -  the hero is the operator, not the technology',
      ],
      products: ['HiveMQ Agentic AI for Operations'],
      footer: (
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/hivemqagentic' })}>
          Explore Agentic AI Operations
          <ArrowRight size={16} />
        </Button>
      ),
    },
  ]

  const unifiedPhases: UnifiedPhaseProps[] = [
    {
      accent: 'blue.solid',
      colorPalette: 'blue',
      icon: PlugZap,
      badgeLabel: 'Experience',
      title: 'Connect',
      description:
        'Bring operational data in from every system: MQTT backbone, industrial protocol translation, extensions & integrations.',
      actionLabel: 'Explore Edge',
      onClick: () => navigate({ to: '/hivemqedge' }),
    },
    {
      accent: 'purple.solid',
      colorPalette: 'purple',
      icon: Tags,
      badgeLabel: 'Experience',
      title: 'Contextualize',
      description:
        'Give data meaning: data & asset modeling, metadata & tags, a semantic graph & ontology, payload governance.',
      actionLabel: 'Explore Data Hub',
      onClick: () => navigate({ to: '/datahub' }),
    },
    {
      accent: 'green.solid',
      colorPalette: 'green',
      icon: Calculator,
      badgeLabel: 'Experience',
      title: 'Analyze',
      description:
        'Turn data into insight: calculations & expressions, a distributed query engine, ML-at-the-edge.',
      actionLabel: 'Explore Data Intelligence',
      onClick: () => navigate({ to: '/pulse' }),
    },
    {
      accent: 'orange.solid',
      colorPalette: 'orange',
      icon: Bot,
      badgeLabel: 'Experience',
      title: 'Act',
      description:
        'Close the loop safely: an industrial agent runtime, agent workflow builder, guardrails & auditing.',
      actionLabel: 'Explore Agentic AI',
      onClick: () => navigate({ to: '/hivemqagentic' }),
    },
  ]

  const progressionSteps: ProgressionStepProps[] = [
    { icon: Radio, title: 'Broker', description: 'MQTT pub/sub at scale' },
    { icon: Cable, title: 'Edge', description: 'Industrial protocol translation' },
    { icon: Workflow, title: 'Data Hub', description: 'Governance + transforms' },
    { icon: Database, title: 'Pulse', description: 'Distributed intelligence' },
    { icon: Bot, title: 'Agentic AI', description: 'Safe closed-loop AI' },
  ]

  const principles: PrincipleCardProps[] = [
    {
      icon: Brain,
      title: 'Trusted, AI-Ready Pipelines',
      description: 'Governed, contextualized data that AI can act on with confidence.',
    },
    {
      icon: Network,
      title: 'Edge to Cloud',
      description: 'Distributed processing without central bottlenecks.',
    },
    {
      icon: Scale,
      title: 'Always-On, Mission-Critical',
      description: 'Seamless growth from POC to millions of data points enterprise-wide.',
    },
    {
      icon: Shield,
      title: 'Governed Data You Can Trust',
      description: 'One version of the truth  -  teams act with confidence, not guesswork.',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        centered
        title="The Platform Journey"
        subtitle="From streaming to intelligence to agentic operations. Help customers progress from AI-ready data streaming, to real-time data intelligence, to autonomous actions at the edge."
      />

      <RevealStack align="stretch" gap={10}>
        {/* Hero diagram */}
        <VStack gap={5} textAlign="center">
          <ZoomableMedia
            src={visionImage}
            alt="HiveMQ Platform Architecture"
            h="500px"
            onZoom={handleZoom}
          />
          <HStack gap={2} flexWrap="wrap" justify="center">
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Connect
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Contextualize
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Analyze
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Act
              </Text>
            </Box>
          </HStack>
        </VStack>

        {/* The Unified AI Platform */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="The Unified AI Platform"
            description="One platform. One experience. One path from device to action."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            {unifiedPhases.map((phase) => (
              <InsightCard key={phase.title} {...phase} />
            ))}
          </SimpleGrid>
          <Card.Root bg="bg.subtle">
            <Card.Body p={5}>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                Cross-cutting capabilities
              </Text>
              <Text fontSize="sm" color="content.secondary">
                MCP for data &amp; operations · Notification center + alert integrations · Billing
                center · Role-Based Access Control
              </Text>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Three Pillars */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="The Platform Journey"
            description="Trusted, AI-ready pipelines that deliver measurable outcomes - from edge to cloud. Three coordinated layers, each building on the last."
          />
          <SimpleGrid columns={{ base: 1, lg: 3 }} gap={6}>
            {pillars.map((pillar) => (
              <PillarCard key={pillar.title} {...pillar} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Our Progression */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Our Progression"
            description="Each launch was a discrete value step. Each prior layer persists and powers everything above it."
          />
          <HStack align="stretch" gap={3} flexWrap="wrap" justify="center">
            {progressionSteps.map((step, i) => (
              <HStack key={step.title} align="stretch" gap={3} flex="1" minW="180px">
                <ProgressionStepCard {...step} />
                {i < progressionSteps.length - 1 ? <ProgressionArrow /> : null}
              </HStack>
            ))}
          </HStack>
          <Text fontSize="sm" color="content.tertiary" fontStyle="italic">
            10+ years, Fortune-500 trusted. The full stack - Broker + Edge + Data Hub + Pulse +
            Agentic AI - comes together in the unified platform.
          </Text>
        </VStack>

        {/* Core Principles */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Built for Enterprise"
            description="Four foundational principles that define the HiveMQ Platform"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
            {principles.map((principle) => (
              <PrincipleCard key={principle.title} {...principle} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Persona Messaging */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Who We Serve"
            description="From hands-on implementers to business leaders - HiveMQ speaks to every stakeholder"
          />
          <SimpleGrid columns={{ base: 1, lg: 3 }} gap={6}>
            <Card.Root h="100%">
              <Card.Body p={6}>
                <VStack align="start" gap={4}>
                  <HStack gap={3}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <Cpu size={20} />
                    </Box>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="bold">OT/IT Engineer</Text>
                      <Text fontSize="xs" color="content.secondary">
                        Hands-on implementers &amp; operators
                      </Text>
                    </VStack>
                  </HStack>
                  <Box px={3} py={1} bg="gray.100" borderRadius="md" _dark={{ bg: 'gray.700' }}>
                    <Text fontSize="sm" fontWeight="medium">
                      Integrate IT/OT
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="content.secondary">
                    Connect and collect data from all assets with MQTT. Stream data reliably without
                    disrupting operations. Bridge legacy OT to modern IT. Deploy flexibly from edge
                    to cloud.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root h="100%">
              <Card.Body p={6}>
                <VStack align="start" gap={4}>
                  <HStack gap={3}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <Network size={20} />
                    </Box>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="bold">IT Architect</Text>
                      <Text fontSize="xs" color="content.secondary">
                        System designers &amp; technical decision-makers
                      </Text>
                    </VStack>
                  </HStack>
                  <Box px={3} py={1} bg="gray.100" borderRadius="md" _dark={{ bg: 'gray.700' }}>
                    <Text fontSize="sm" fontWeight="medium">
                      Data-Driven Backbone
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="content.secondary">
                    Unify and govern OT and IT data. Build event-driven and UNS-based architectures.
                    Integrate data with cloud, analytics, and AI platforms. Scale from one use case
                    to enterprise standard.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root h="100%">
              <Card.Body p={6}>
                <VStack align="start" gap={4}>
                  <HStack gap={3}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <Brain size={20} />
                    </Box>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="bold">Business Leader</Text>
                      <Text fontSize="xs" color="content.secondary">
                        Outcome owners &amp; budget holders
                      </Text>
                    </VStack>
                  </HStack>
                  <Box px={3} py={1} bg="gray.100" borderRadius="md" _dark={{ bg: 'gray.700' }}>
                    <Text fontSize="sm" fontWeight="medium">
                      Transform to Agentic AI
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="content.secondary">
                    Improve uptime, efficiency, and visibility. Turn operational data into trusted
                    intelligence. Enable distributed, AI-ready pipelines. Move from streaming to
                    intelligence to agentic operations.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Ready to learn more?</Heading>
                <Text color="content.secondary">
                  Discover why leading enterprises choose HiveMQ for their industrial data platform
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/hivemqpilars' })}>
                Discover Why HiveMQ
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </RevealStack>

      {/* Zoom Modal */}
      <Dialog.Root
        open={zoomMedia !== null}
        onOpenChange={(details) => {
          if (!details.open) {
            handleCloseZoom()
          }
        }}
        size="cover"
        placement="center"
        motionPreset="scale"
      >
        <Portal>
          <Dialog.Backdrop bg="blackAlpha.800" />
          <Dialog.Positioner>
            <Dialog.Content
              bg="transparent"
              shadow="none"
              maxW="95vw"
              maxH="95vh"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Dialog.CloseTrigger asChild position="absolute" top={4} right={4}>
                <CloseButton
                  aria-label="Close zoomed image"
                  size="lg"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  borderRadius="full"
                />
              </Dialog.CloseTrigger>
              <Dialog.Body
                p={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={handleCloseZoom}
              >
                <Image
                  src={zoomMedia?.src}
                  alt={zoomMedia?.alt || ''}
                  maxW="90vw"
                  maxH="90vh"
                  objectFit="contain"
                  borderRadius="lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}
